import React, { useEffect } from 'react';
import { 
  Box, 
  Grid, 
  VStack, 
  Heading, 
  Text,
  SimpleGrid,
  HStack,
  Icon,
  Flex,
  useColorModeValue,
  Circle,
  Progress
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Brain, Heart, Activity, MessageCircle, CheckCircle } from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import UnifiedMobilePlatform from '../ui-samples/UnifiedMobilePlatform';
import { useAudio } from '../../context/AudioContext';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#805AD5';

/**
 * Presenter's Guide for ResilienceDeepDive Slide:
 * 
 * 1. Introduction (30 seconds):
 *    - Begin by introducing the Adaptability Quotient (AQ) Domain as the first pillar of the JIMI Platform
 *    - Emphasize that this domain focuses on building personal resilience for clinicians
 *    - Highlight that this is the foundation for preventing burnout and improving wellness
 * 
 * 2. Key Features Section (45 seconds):
 *    - Point out the four key features of the AQ Domain:
 *      a. AI Companion: Personalized support through natural conversation
 *      b. Wellness Dashboard: Easy visualization of personal metrics
 *      c. Smart Guidance: Tailored recommendations based on individual patterns
 *      d. Digital Persona: Adaptive tracking of progress over time
 * 
 * 3. Metrics & Benefits (30 seconds):
 *    - Explain the three key wellness metrics tracked in the platform
 *    - Emphasize the five major benefits for clinicians, focusing on burnout reduction
 * 
 * 4. Implementation Approach (30 seconds):
 *    - Walk through the three-step implementation process
 *    - Stress that this is a personalized journey for each clinician
 * 
 * 5. Mobile Experience Demo (1 minute):
 *    - Introduce the mobile interface as the primary way clinicians will interact with the platform
 *    - Guide the audience through the different tabs and features visible in the demo
 *    - Highlight how the interface provides real-time support and resources
 * 
 * 6. Conclusion (15 seconds):
 *    - Summarize how the AQ Domain builds resilience through personalized digital interventions
 *    - Transition to the next slide by mentioning that we'll explore the Culture of Wellness Digital Interface next
 */

// Full narration script for TTS Studio by 11Labs
const narrationScript = `Welcome to an in-depth exploration of the Adaptability Quotient Domain, the first pillar of our JIMI Platform's Professional Fulfillment Model.

The SentientIQ AQ platform focuses on building clinician resilience through personalized digital interventions, real-time wellness monitoring, and adaptive support systems. This domain forms the foundation of our approach to preventing burnout and improving overall wellness for healthcare professionals.

Let me guide you through the four key features that power the AQ Domain:

First, the AI Companion provides personalized support through natural conversation, adapting to each clinician's unique needs and circumstances.

Second, our Wellness Dashboard offers easy-to-understand visualization of personal wellness metrics and progress, making data actionable and meaningful.

Third, Smart Guidance delivers tailored recommendations based on individual wellness patterns and needs, ensuring relevance and effectiveness.

And fourth, the Digital Persona tracks personal progress and adapts to changing wellness needs over time, creating a truly personalized experience.

We track three essential wellness metrics that provide clinicians with a comprehensive view of their progress:
- Stress Resilience, currently at 72 out of 80
- Mindfulness, measuring 65 out of 75
- And Recovery Rate, showing strong performance at 81 out of 85

These features translate into five tangible benefits for clinicians:
- Reduced burnout through personalized interventions
- Increased self-awareness of stress patterns
- Actionable wellness strategies that fit into busy schedules
- Improved resilience through micro-wellness activities
- And a data-driven approach to personal wellness management

Our implementation follows a proven three-step approach:
First, we begin with a Personal Assessment to establish baseline metrics through a comprehensive wellness evaluation.
Then, we develop a Personalized Plan with AI-generated wellness activities and goals tailored to individual needs.
Finally, we ensure Continuous Adaptation as you progress, dynamically adjusting plans based on your changing needs and achievements.

On the right, you can see how clinicians will experience the AQ Domain through our mobile interface. This is the SentientIQ AQ Dashboard, where clinicians can view their Adaptability Quotient score and key wellness metrics at a glance. The interface provides personalized resilience insights and recommended actions based on their individual data.

The mobile experience ensures that wellness resources are accessible anytime, anywhere, fitting seamlessly into a clinician's busy day.

The Adaptability Quotient Domain builds resilience through personalized digital interventions, real-time wellness monitoring, and adaptive support systems. This forms the foundation of our Professional Fulfillment Model.

In our next slide, we'll explore the Culture of Wellness Digital Interface, which builds upon this foundation to create supportive community connections for clinicians.`;

const ResilienceDeepDive: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  // Use the audio context
  const { playAudioForSlide } = useAudio();

  // Play audio when component mounts
  useEffect(() => {
    // Play audio with a slight delay to ensure DOM is ready
    const audioTimer = setTimeout(() => {
      console.log('Starting audio playback for resilience slide...');
      playAudioForSlide('resilience');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  // Features data
  const features = [
    {
      icon: Brain,
      title: "AI Companion",
      description: "Personal wellness support and guidance powered by advanced AI",
      color: methodistBlue,
      impactMetric: "92% engagement",
      scalabilityInfo: "Personalized support at scale"
    },
    {
      icon: Heart,
      title: "Health Vitals",
      description: "Track and monitor key wellness metrics for proactive health management",
      color: methodistGreen,
      impactMetric: "78% improvement",
      scalabilityInfo: "Real-time health insights"
    },
    {
      icon: Activity,
      title: "Smart Guidance",
      description: "Personalized recommendations based on individual wellness patterns",
      color: methodistPurple,
      impactMetric: "85% satisfaction",
      scalabilityInfo: "AI-driven recommendations"
    },
    {
      icon: MessageCircle,
      title: "Digital Persona",
      description: "Track personal progress and wellness journey milestones",
      color: methodistNavy,
      impactMetric: "88% completion",
      scalabilityInfo: "Customized growth tracking"
    }
  ];

  return (
    <MethodistSlide 
      showLogo={true}
      captionsText={narrationScript}
      isSpeaking={undefined}
      showCaptions={undefined}
    >
      <Grid templateColumns="2fr 1fr" gap={8} h="100%" py={6}>
        {/* Left side: Features and content */}
        <VStack align="stretch" spacing={8}>
          {/* Introduction */}
          <Box>
            <Heading size="md" color={methodistBlue} mb={3}>
              Adaptability Quotient (AQ) Domain
            </Heading>
            <Text fontSize="md" color={textColor}>
              The SentientIQ Adaptability Quotient (AQ) platform empowers clinicians with AI-driven personal wellness support
              and guidance. Our mobile interface provides seamless access to wellness tools and insights anytime, anywhere.
            </Text>
          </Box>

          {/* Key Features */}
          <Box>
            <Heading size="sm" mb={4}>Key Features</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {features.map((feature, index) => (
                <HStack
                  key={index}
                  spacing={4}
                  p={4}
                  bg={bgColor}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  shadow="sm"
                  as={motion.div}
                  whileHover={{ scale: 1.02 }}
                  transition={{ ease: "easeInOut" }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    bg={`${feature.color}15`}
                    color={feature.color}
                    borderRadius="md"
                    p={2}
                    boxSize="40px"
                  >
                    <Icon as={feature.icon} boxSize={5} />
                  </Flex>
                  <VStack align="start" spacing={1} flex={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      {feature.title}
                    </Text>
                    <Text fontSize="xs" color={textColor}>
                      {feature.description}
                    </Text>
                    <HStack fontSize="xs" spacing={3} mt={1}>
                      <Text color={feature.color} fontWeight="medium">
                        {feature.impactMetric}
                      </Text>
                      <Text color="gray.500">
                        {feature.scalabilityInfo}
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* Implementation Steps and Benefits Grid */}
          <SimpleGrid columns={2} spacing={8}>
            {/* Left Column: Implementation Steps */}
            <Box>
              <Heading size="sm" mb={4}>Implementation Approach</Heading>
              <VStack spacing={4}>
                {[
                  {
                    title: "Personal Assessment",
                    description: "Establish baseline metrics through comprehensive wellness evaluation",
                    color: methodistBlue
                  },
                  {
                    title: "Personalized Plan",
                    description: "AI-generated wellness activities and goals tailored to individual needs",
                    color: methodistGreen
                  },
                  {
                    title: "Continuous Adaptation",
                    description: "Dynamic adjustment of plans based on progress and changing needs",
                    color: methodistPurple
                  }
                ].map((step, index) => (
                  <Box
                    key={index}
                    p={4}
                    bg={bgColor}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    shadow="sm"
                    w="full"
                    as={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <HStack spacing={4} align="start">
                      <Circle size="24px" bg={`${step.color}15`}>
                        <Text fontSize="xs" fontWeight="bold" color={step.color}>
                          {index + 1}
                        </Text>
                      </Circle>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontWeight="semibold"
                          fontSize="sm"
                          color={step.color}
                        >
                          {step.title}
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          {step.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Right Column: Benefits and Metrics */}
            <VStack spacing={6}>
              {/* Clinician Benefits */}
              <Box w="full">
                <Heading size="sm" mb={4}>Clinician Benefits</Heading>
                <VStack 
                  align="start" 
                  spacing={3} 
                  bg={bgColor} 
                  p={4} 
                  borderRadius="md" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                  shadow="sm"
                >
                  {[
                    "Reduced burnout through personalized interventions",
                    "Increased self-awareness of stress patterns",
                    "Actionable wellness strategies for busy schedules",
                    "Improved resilience through micro-wellness activities",
                    "Data-driven personal wellness management"
                  ].map((benefit, index) => (
                    <HStack key={index} spacing={3} w="full">
                      <Circle size="16px" bg={`${methodistBlue}15`}>
                        <Icon as={CheckCircle} boxSize={3} color={methodistBlue} />
                      </Circle>
                      <Text fontSize="xs" color={textColor}>
                        {benefit}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* Key Metrics */}
              <Box w="full">
                <Heading size="sm" mb={4}>Key Metrics</Heading>
                <HStack spacing={3}>
                  {[
                    {
                      label: "Stress Resilience",
                      value: "72",
                      goal: "80",
                      color: methodistBlue,
                      icon: Brain
                    },
                    {
                      label: "Mindfulness",
                      value: "65",
                      goal: "75",
                      color: methodistGreen,
                      icon: Heart
                    },
                    {
                      label: "Recovery Rate",
                      value: "81",
                      goal: "85",
                      color: methodistPurple,
                      icon: Activity
                    }
                  ].map((metric, index) => (
                    <Box
                      key={index}
                      p={3}
                      bg={bgColor}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                      shadow="sm"
                      flex={1}
                    >
                      <VStack spacing={2}>
                        <Icon as={metric.icon} color={metric.color} boxSize={4} />
                        <Text fontSize="xs" color={textColor} noOfLines={1}>
                          {metric.label}
                        </Text>
                        <HStack spacing={1}>
                          <Text fontSize="sm" fontWeight="bold" color={metric.color}>
                            {metric.value}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            /{metric.goal}
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </HStack>
              </Box>
            </VStack>
          </SimpleGrid>
        </VStack>

        {/* Right side: Mobile UI Demo */}
        <VStack align="start" spacing={4}>
          <Box>
            <Heading size="sm" mb={2}>Mobile Platform Demo</Heading>
            <Text fontSize="sm" color={textColor}>
              Experience the SentientIQ mobile interface in action. Our intuitive design provides seamless access to wellness tools, 
              personalized insights, and progress tracking—all optimized for busy healthcare professionals.
            </Text>
          </Box>
          <Box 
            bg={bgColor}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            shadow="sm"
            p={4}
            w="full"
          >
            <UnifiedMobilePlatform 
              activeDomain="AQ"
              defaultTab="activities"
            />
          </Box>
        </VStack>
      </Grid>
    </MethodistSlide>
  );
};

export default ResilienceDeepDive;
